import { requireDataClassName, resolveDataClass } from './entity-serializer'
import {
  getEntityRegistry,
  isEntityEnabled,
} from '@/Atomic/Widgets/AiChatBot/core/engine/entity-registry'
import type { EntityClassName } from '@/Atomic/Widgets/AiChatBot/core/engine/types'

type ResolvedJunction = {
  kind: 'junction'
  /** Junction DataClass (e.g. ActivityCompanyRelation) — query this for counts/rows */
  jdc: NonNullable<ReturnType<typeof resolveDataClass>>
  /** Filters to apply to the junction table */
  junctionFilters: Record<string, string>
  /** Field on the junction row that holds the target entity _id */
  idField: string
  /**
   * Field on the junction row that holds the PARENT entity _id. For symmetric
   * junctions (ContactContactRelation) PISA's parent filter returns rows with
   * the parent on either side, so the target may sit here instead of idField —
   * get_related mirrors getRelatedContact and reads whichever side is not the
   * parent.
   */
  parentFilterKey: string
  /** Target DataClass (e.g. Activity) — use for fetching full records */
  dc: NonNullable<ReturnType<typeof resolveDataClass>>
  targetDataClass: EntityClassName
  /** Relation-specific default sort order from registry (e.g. [['beginAt', 'desc']]) */
  defaultOrder: Array<[string, 'asc' | 'desc']>
}

type ResolvedDirect = {
  kind: 'direct'
  /** Target DataClass — query this directly */
  dc: NonNullable<ReturnType<typeof resolveDataClass>>
  /** Filter key to apply on the target DataClass */
  filters: Record<string, string>
  targetDataClass: EntityClassName
  /** Relation-specific default sort order from registry (e.g. [['beginAt', 'desc']]) */
  defaultOrder: Array<[string, 'asc' | 'desc']>
}

export type ResolvedRelation = ResolvedJunction | ResolvedDirect

export type RelationResolveError = { error: string }

/**
 * Resolves a registry relation into either a junction or direct-filter scope.
 * Use this in any tool that needs to query or count related entities — never
 * inline the junction/direct branch yourself.
 */
export function resolveRelation(
  parentDataClass: string,
  parentId: string,
  relation: string,
  featureFlags: Set<string>,
): ResolvedRelation | RelationResolveError {
  const parentClass = requireDataClassName(parentDataClass)
  const meta = parentClass ? getEntityRegistry()[parentClass] : undefined
  if (!meta) {
    return { error: `Unknown parent entity type: ${parentDataClass}` }
  }

  const relationSpec = meta.relations[relation]
  if (!relationSpec) {
    return {
      error: `Unknown relation "${relation}" for ${parentDataClass}. Available: ${Object.keys(meta.relations).join(', ')}.`,
    }
  }

  const { dataClass: targetDataClass, filterKey, viaJunction } = relationSpec

  // Relations whose target entity is disabled on this instance are not
  // available — mirrors the detail-page tab gate. Guards against the model
  // calling a relation (e.g. tickets, opportunities) the instance lacks.
  if (!isEntityEnabled(targetDataClass, featureFlags)) {
    return {
      error: `The ${targetDataClass} entity is not available on this instance, so the "${relation}" relation cannot be fetched.`,
    }
  }

  if (viaJunction) {
    const jdc = resolveDataClass(viaJunction.junctionClass)
    if (!jdc) {
      return {
        error: `No DataClass found for junction ${viaJunction.junctionClass}`,
      }
    }
    const dc = resolveDataClass(targetDataClass)
    if (!dc) {
      return { error: `No DataClass found for ${targetDataClass}` }
    }
    const junctionFilters: Record<string, string> = {
      [viaJunction.parentFilterKey]: parentId,
      ...(viaJunction.parentFilterExtra ?? {}),
    }
    return {
      kind: 'junction',
      jdc,
      junctionFilters,
      idField: viaJunction.idField,
      parentFilterKey: viaJunction.parentFilterKey,
      dc,
      targetDataClass,
      defaultOrder: relationSpec.defaultOrder ?? [['updatedAt', 'desc']],
    }
  }

  if (!filterKey) {
    return { error: `Relation "${relation}" has no filterKey or viaJunction` }
  }
  const dc = resolveDataClass(targetDataClass)
  if (!dc) {
    return { error: `No DataClass found for ${targetDataClass}` }
  }
  return {
    kind: 'direct',
    dc,
    filters: { [filterKey]: parentId },
    targetDataClass,
    defaultOrder: relationSpec.defaultOrder ?? [['updatedAt', 'desc']],
  }
}
